import { useTranslation } from 'react-i18next';
import { ArrowRight, Gauge } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface CapacityCardProps {
  /** Shopify orders placed for the location in the window (demand). */
  ordersPlaced: number;
  /** Orders shipped/fulfilled in the window (throughput). */
  ordersFulfilled: number;
  /** Units across orders placed in the window (demand). */
  unitsOrdered: number;
  /** Garments printed in the window (throughput). */
  unitsPrinted: number;
  isLoading?: boolean;
  /** Short range label shown as the card subtitle. */
  dateRangeLabel?: string;
}

/**
 * Capacity card — demand vs output for DTG capacity planning. Two rows (Orders,
 * Units) each showing demand → throughput with a coverage % that says, at a
 * glance, whether production kept up with the orders that came in over the
 * window. Sits in the spare cell of the summary-cards grid.
 *
 * Coverage = throughput / demand. >= 90% reads as keeping up (emerald),
 * 75–90% as slipping (amber), below 75% as behind (rose).
 */
function coverageBand(pct: number | null): { text: string; chip: string } {
  if (pct == null) {
    return { text: 'text-muted-foreground', chip: 'bg-muted text-muted-foreground ring-1 ring-border' };
  }
  if (pct >= 90) {
    return { text: 'text-emerald-600', chip: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-300/70 dark:bg-emerald-950/40 dark:text-emerald-300' };
  }
  if (pct >= 75) {
    return { text: 'text-amber-600', chip: 'bg-amber-50 text-amber-700 ring-1 ring-amber-300/70 dark:bg-amber-950/40 dark:text-amber-300' };
  }
  return { text: 'text-rose-600', chip: 'bg-rose-50 text-rose-700 ring-1 ring-rose-300/70 dark:bg-rose-950/40 dark:text-rose-300' };
}

function CapacityRow({ label, demand, output }: { label: string; demand: number; output: number }) {
  const pct = demand > 0 ? (output / demand) * 100 : null;
  const band = coverageBand(pct);
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="w-14 shrink-0 text-muted-foreground">{label}</span>
      <span className="flex-1 text-right tabular-nums">
        <span className="font-semibold">{demand.toLocaleString()}</span>
        <ArrowRight className="mx-1 inline h-3 w-3 text-muted-foreground" aria-hidden="true" />
        <span className="font-semibold">{output.toLocaleString()}</span>
      </span>
      <span
        className={cn(
          'inline-flex w-11 shrink-0 justify-center rounded-md px-1 py-0.5 text-xs font-medium tabular-nums',
          band.chip,
        )}
      >
        {pct == null ? '-' : `${Math.round(pct)}%`}
      </span>
    </div>
  );
}

export function CapacityCard({
  ordersPlaced,
  ordersFulfilled,
  unitsOrdered,
  unitsPrinted,
  isLoading,
  dateRangeLabel,
}: CapacityCardProps) {
  const { t } = useTranslation('production');
  return (
    <Card
      className={cn(
        'relative overflow-hidden transition-shadow hover:shadow-sm',
        "before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:content-['']",
        'before:bg-blue-500',
      )}
    >
      <CardContent className="pt-5 pb-4 pl-6 pr-5">
        <div className="mb-3 flex items-start justify-between gap-3">
          <span className="text-sm font-medium leading-tight text-muted-foreground">
            {t('qa.capacity.title', 'Demand vs output')}
          </span>
          <span
            className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-blue-50 dark:bg-blue-950/40"
            aria-hidden="true"
          >
            <Gauge className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </span>
        </div>

        {isLoading ? (
          <div className="space-y-2.5">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
          </div>
        ) : (
          <div className="space-y-2">
            <CapacityRow
              label={t('qa.capacity.orders', 'Orders')}
              demand={ordersPlaced}
              output={ordersFulfilled}
            />
            <CapacityRow
              label={t('qa.capacity.units', 'Units')}
              demand={unitsOrdered}
              output={unitsPrinted}
            />
          </div>
        )}

        {!isLoading && (
          <p className="mt-2.5 text-[11px] leading-tight text-muted-foreground">
            {t('qa.capacity.legend', 'Placed → fulfilled · ordered → printed')}
            {dateRangeLabel ? ` · ${dateRangeLabel}` : ''}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
